import React from 'react';
import { ClinicalNote } from '../../types';
type AmendmentHistoryProps = {
  note: ClinicalNote;
};
const formatDateTime = (value: string) => new Date(value).toLocaleString('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
});

/** Supplementary notes and the full, append-only amendment trail. */
export function AmendmentHistory({
  note
}: AmendmentHistoryProps) {
  const addenda = note.addenda ?? [];
  const history = [...(note.editHistory ?? [])].reverse();
  return <div className="grid grid-cols-1 gap-5 lg:grid-cols-2" data-id="element-5850" data-mp="doji4590uy5s">
      <section aria-labelledby="addenda-heading" className="rounded-xl border border-gray-200 bg-white" data-id="element-5851" data-mp="sdz6jel2yrib">
        <h3 id="addenda-heading" className="border-b border-gray-100 px-5 py-3 font-semibold text-gray-900" data-id="element-5852" data-mp="nfnx34yp6j0m">
          Supplementary notes{' '}
          <span className="font-normal text-gray-500" data-id="element-5853" data-mp="11cb8udvb7dql">({addenda.length})</span>
        </h3>
        {addenda.length === 0 ? <p className="px-5 py-6 text-sm italic text-gray-400" data-id="element-5854" data-mp="2sjnr7lfysnqd">
            No supplementary notes yet.
          </p> : <ol className="divide-y divide-gray-100" data-id="element-5855" data-mp="2xjjxjrc4wr93">
            {addenda.map(item => <li key={item.id} className="px-5 py-3" data-id="element-5856" data-mp="32gk19vlihhx1">
                <p className="whitespace-pre-line text-sm text-gray-900" data-id="element-5857" data-mp="32jy1ax6pdf1b">{item.text}</p>
                <p className="mt-1 text-xs text-gray-500" data-id="element-5858" data-mp="37eljkeb45d7g">
                  Added by {item.addedByName} · {formatDateTime(item.addedAt)}
                </p>
              </li>)}
          </ol>}
      </section>

      <section aria-labelledby="history-heading" className="rounded-xl border border-gray-200 bg-white" data-id="element-5859" data-mp="3l8cw7vk55d7e">
        <h3 id="history-heading" className="border-b border-gray-100 px-5 py-3 font-semibold text-gray-900" data-id="element-5860" data-mp="3q7pee302hw3n">
          Amendment history{' '}
          <span className="font-normal text-gray-500" data-id="element-5861" data-mp="3v7tv9u4w0sue">({history.length})</span>
        </h3>
        <p className="border-b border-gray-100 px-5 py-2 text-xs text-gray-500" data-id="element-5862" data-mp="32ztq26svrr9">
          Original entry by {note.clinicianName || 'unknown clinician'} on{' '}
          {note.recordedAt ? formatDateTime(note.recordedAt) : 'unknown date'}
        </p>
        {history.length === 0 ? <p className="px-5 py-6 text-sm italic text-gray-400" data-id="element-5863" data-mp="28wfwvqju5liq">
            This file has not been amended.
          </p> : <ol className="max-h-96 divide-y divide-gray-100 overflow-y-auto" data-id="element-5864" data-mp="242l8i9tacidb">
            {history.map((edit, index) => <li key={`${edit.editedAt}-${index}`} className="px-5 py-3 text-sm" data-id="element-5865" data-mp="2dur9g91ima4n" data-mpi={index}>
                <p className="font-medium text-gray-900" data-id="element-5866" data-mp="2dwv92esdsmz4">
                  {edit.editedByName}{' '}
                  <span className="font-normal text-gray-500" data-id="element-5867" data-mp="2nsoxavfgq4wq">
                    · {formatDateTime(edit.editedAt)}
                  </span>
                </p>
                <p className="mt-0.5 text-gray-700" data-id="element-5868" data-mp="xgrc95u20yl3">
                  <span className="text-gray-500" data-id="element-5869" data-mp="texf37j2icpo">Reason:</span> {edit.reason}
                </p>
                {edit.changes && edit.changes.length > 0 && <details className="mt-1.5" data-id="element-5870" data-mp="jbiktck8d0hu">
                    <summary className="cursor-pointer text-xs font-medium text-blue-700" data-id="element-5871" data-mp="ncu83bfmt9mv">
                      {edit.changes.length} field
                      {edit.changes.length === 1 ? '' : 's'} corrected
                    </summary>
                    <ul className="mt-2 space-y-2" data-id="element-5872" data-mp="ei60vfqhxhvf">
                      {edit.changes.map(change => <li key={change.field} data-id="element-5873" data-mp="3vu8h7acpvtz5">
                          <p className="text-xs font-semibold text-gray-700" data-id="element-5874" data-mp="4m30aebalral">{change.label}</p>
                          <p className="whitespace-pre-line text-xs text-red-700 line-through decoration-red-300" data-id="element-5875" data-mp="3mrz54g1svsjn">
                            {change.previous || '(blank)'}
                          </p>
                          <p className="whitespace-pre-line text-xs text-emerald-700" data-id="element-5876" data-mp="1kzt4rm6bqhkr">
                            {change.amended || '(blank)'}
                          </p>
                        </li>)}
                    </ul>
                  </details>}
                {edit.addendum && <p className="mt-1 text-xs text-gray-500" data-id="element-5877" data-mp="3h3qt7b2lc8zq">+ Supplementary note added</p>}
                {!edit.changes && !edit.addendum && <p className="mt-1 text-xs text-gray-500" data-id="element-5878" data-mp="383asuah59hz3">
                    Edited during consultation — full previous version retained.
                  </p>}
              </li>)}
          </ol>}
      </section>
    </div>;
}